import React from 'react';
import { Box, Typography } from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import EntityDetailDrawer from '../EntityDetailDrawer';
import { Section, Field, BulletList, Code } from '../Ontology/DetailPrimitives';
import { TierPill } from './BenchmarkPrimitives';
import { ScoredAlert } from '../../services/benchmarkService';
import {
    featureName, humanise, outcomeColor, outcomeLabel, signalPhrase, scenarioName
} from './BenchmarkVocabulary';
import { dsVar, ink } from '../../theme/tokens';

interface RescuedCaseDrawerProps {
    open: boolean;
    alert: ScoredAlert | null;
    onClose: () => void;
}

/** Values from the feature snapshot, rendered so a boolean reads as a sentence. */
const snapshotValue = (value: unknown): string => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
};

/**
 * One alert, side by side: where your rules put it, where the agent put it, and
 * what investigators found.
 *
 * Built on EntityDetailDrawer so it docks like every other detail panel in the
 * app — the list stays on screen and keeps its filters and page while the reader
 * works through cases. It was previously a separate 540px overlay drawer that
 * covered the table it came from.
 */
const RescuedCaseDrawer: React.FC<RescuedCaseDrawerProps> = ({ open, alert, onClose }) => {
    if (!alert) {
        return (
            <EntityDetailDrawer
                open={false}
                onClose={onClose}
                title=""
                description=""
                docked
                dockedMinWidth={1280}
            />
        );
    }

    const signals = alert.elevatingSignals ?? [];
    const breakdown = Object.entries(alert.scoreBreakdown ?? {})
        .filter(([, value]) => typeof value === 'number');
    const snapshot = Object.entries(alert.featureSnapshot ?? {});

    return (
        <EntityDetailDrawer
            open={open}
            onClose={onClose}
            docked
            dockedMinWidth={1280}
            icon={<FlagOutlinedIcon />}
            subtitle={scenarioName(alert.typology)}
            title={alert.alertId}
            // The drawer renders this as markdown; it is the one-sentence summary
            // of what changed for this alert.
            description={
                alert.isRescued
                    ? `Your current rules gave this alert **low priority**. The agent raised it, and investigators went on to record it as **${outcomeLabel(alert.outcomeClass).toLowerCase()}**.`
                    : `Investigators recorded this alert as **${outcomeLabel(alert.outcomeClass).toLowerCase()}**.`
            }
            banner={
                <Box className="flex flex-wrap items-center gap-2 mb-3">
                    <TierPill code={alert.incumbentTier} />
                    <Typography component="span" sx={{ color: dsVar('fg-muted'), fontSize: '0.875rem' }}>→</Typography>
                    <TierPill code={alert.agentTier} />
                    <Box
                        component="span"
                        sx={{
                            px: 1, py: 0.3, borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 700,
                            color: ink(outcomeColor(alert.outcomeClass)),
                            backgroundColor: `${outcomeColor(alert.outcomeClass)}22`,
                            border: `1px solid ${outcomeColor(alert.outcomeClass)}55`
                        }}
                    >
                        {outcomeLabel(alert.outcomeClass)}
                    </Box>
                    {alert.sarFiled && (
                        <Box
                            component="span"
                            sx={{
                                px: 1, py: 0.3, borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 700,
                                color: ink('#FBBF24'), backgroundColor: 'rgba(251, 191, 36, 0.13)',
                                border: '1px solid rgba(251, 191, 36, 0.35)'
                            }}
                        >
                            SAR filed
                        </Box>
                    )}
                </Box>
            }
        >
            <Section title="Why the agent raised it">
                {signals.length > 0 ? (
                    <BulletList items={signals.map((s) => signalPhrase(s.code, s.value))} />
                ) : (
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-muted') }}>
                        No single signal stood out — the agent's score came from the combination.
                    </Typography>
                )}
            </Section>

            <Section title="Priority scores">
                <Field label="Agent score" value={alert.agentScore ?? '—'} />
                <Field label="Score under your current rules" value={alert.incumbentScore ?? '—'} />
            </Section>

            {/*
              Rendered from whatever the run recorded, rather than four fixed
              layers with default scores of 20/45/0/15 that appeared even when
              the alert carried no breakdown at all.
            */}
            {breakdown.length > 0 && (
                <Section title="What made up the score">
                    {breakdown.map(([key, value]) => (
                        <Field key={key} label={featureName(key)} value={`${value} points`} />
                    ))}
                </Section>
            )}

            <Section title="Case details">
                <Field label="Customer" value={<Code>{alert.customerRef}</Code>} />
                <Field label="Scenario" value={scenarioName(alert.typology)} />
                <Field label="Scenario code" value={<Code>{alert.typology}</Code>} />
                <Field label="Period evaluated" value={alert.configEpoch} />
            </Section>

            {snapshot.length > 0 && (
                <Section title="What the agent saw at the time">
                    {snapshot.map(([key, value]) => (
                        <Field key={key} label={featureName(key) || humanise(key)} value={snapshotValue(value)} />
                    ))}
                </Section>
            )}
        </EntityDetailDrawer>
    );
};

export default RescuedCaseDrawer;
